import { useState } from "react";
import { Eye, EyeSlash, Check, X } from "@phosphor-icons/react";

/**
 * v3.2.19 — Reusable password input with a visibility toggle. Wraps a
 * native <input> and adds an eye/eye-slash button on the right that
 * flips `type` between "password" and "text". All native props (value,
 * onChange, required, minLength, placeholder, data-testid, className)
 * pass through to the underlying <input>.
 *
 * v3.2.20 — Optional live strength meter when `showStrength` is true.
 * Renders 3 rule pills (10+ chars · letter + digit · special char).
 */

const SPECIALS = "!@#$%^&*()-_=+[]{};:'\",.<>/?\\|`~";

export function checkPasswordPolicy(pw) {
  const s = pw || "";
  const okLen     = s.length >= 10;
  const okLetter  = /[A-Za-z]/.test(s);
  const okDigit   = /\d/.test(s);
  const okSpecial = [...s].some((c) => SPECIALS.includes(c));
  return {
    okLen, okLetter, okDigit, okSpecial,
    allOk: okLen && okLetter && okDigit && okSpecial,
  };
}

function Rule({ ok, children, testid }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 border ${
      ok ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/5"
         : "border-white/10 text-gray-500 bg-white/[0.02]"
    }`}
    data-testid={testid}>
      {ok ? <Check size={11} weight="bold" /> : <X size={11} weight="bold" />}
      {children}
    </span>
  );
}

export default function PasswordInput({
  value,
  onChange,
  required = false,
  minLength,
  placeholder = "••••••••",
  className = "",
  autoComplete = "current-password",
  "data-testid": testid = "password-input",
  showStrength = false,
  ...rest
}) {
  const [visible, setVisible] = useState(false);
  const policy = checkPasswordPolicy(value);
  return (
    <>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          required={required}
          minLength={minLength}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className={`w-full bg-black/40 border border-white/10 pl-3 pr-11 py-3 text-white font-mono text-sm focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF] ${className}`}
          data-testid={testid}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? "Hide password" : "Show password"}
          title={visible ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#00E5FF] transition-colors"
          data-testid={`${testid}-toggle`}
        >
          {visible
            ? <EyeSlash size={18} weight="regular" />
            : <Eye      size={18} weight="regular" />}
        </button>
      </div>
      {showStrength && value !== undefined && value !== "" && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`${testid}-strength`}>
          <Rule ok={policy.okLen}     testid={`${testid}-rule-len`}>10+ chars</Rule>
          <Rule ok={policy.okLetter}  testid={`${testid}-rule-letter`}>letter</Rule>
          <Rule ok={policy.okDigit}   testid={`${testid}-rule-digit`}>digit</Rule>
          <Rule ok={policy.okSpecial} testid={`${testid}-rule-special`}>special char</Rule>
        </div>
      )}
    </>
  );
}
